import { gql } from '@apollo/client'

import { DOCUMENT_SIGNATURE_ATTRIBUTES } from '../documentSignatures/graphQL'

export const WORKER_SNAPSHOT_ATTRIBUTES = gql`
  fragment workerSnapshotAttributes on WorkerSnapshot {
    id
    dob
    trade
    mobile
    address
    emergencyContact
    emergencyContactNumber
    emergencyContactRelationship
    employmentStatus
    medicalInformation
    allergies
    user {
      id
      email
      firstName
      lastName
      userIdentity
    }
    tickets {
      id
      expiry
      number
      obtained
      builderGenerated
      builderId
      countryIssued
      stateIssued
      ticketTemplate {
        id
        code
        name
        expiry
        obtained
        shortName
        ticketTypeId
        ticketCategoryId
      }
      files {
        id
        file {
          id
          name
          url
          contentType
        }
      }
    }
    subcontractors {
      id
      name
      email
      builderId
    }
  }
`

export const SITE_INDUCTION_ATTRIBUTES = gql`
  fragment siteInductionAttributes on ProjectInduction {
    id
    createdAt
    updatedAt
    archivedAt

    workerSnapshot: workerSnapshotFull {
      ...workerSnapshotAttributes
    }
    signatureUrl

    evidenceSighted

    templateName
    templateResponse {
      id
      responseData
    }

    project {
      id
      name
      status
      integrationConnections {
        integration {
          integrationType
        }
      }
    }

    type
    status
    worker {
      id
      mobile
      builderGenerated
      user {
        firstName
        lastName
        email
      }
      tickets {
        id
        updatedAt
        latestReviewStatus
        ticketTemplate {
          name
          shortName
        }
      }
      subcontractors {
        id
        company {
          name
        }
      }
      trade
    }
    subcontractor {
      id
      isBuilder
      company {
        id
        name
      }
    }
    documentSignatures {
      ...documentSignatureAttributes
    }
    currentVersion {
      version
    }
    versionHistory {
      version
      induction {
        id
        status
        type
        createdAt
        archivedAt
      }
    }
    identifier
  }
  ${DOCUMENT_SIGNATURE_ATTRIBUTES}
  ${WORKER_SNAPSHOT_ATTRIBUTES}
`

export const SITE_INDUCTION_ATTRIBUTES_SUBSET = gql`
  fragment siteInductionAttributesSubset on ProjectInduction {
    id
    createdAt
    type

    project {
      name
    }

    status
    worker {
      id
      user {
        firstName
        lastName
      }
      tickets {
        ticketTemplate {
          shortName
        }
        reviews {
          reviewStatus
        }
      }
    }
    subcontractor {
      id
      company {
        id
        name
      }
    }
    currentVersion {
      version
    }
    versionHistory {
      version
    }
    identifier

    templateResponse {
      tableBlocks
    }
  }
`

export const COMPANY_INDUCTION_ATTRIBUTES = gql`
  fragment companyInductionAttributes on BuilderInduction {
    id
    createdAt
    updatedAt
    archivedAt

    workerSnapshot: workerSnapshotFull {
      ...workerSnapshotAttributes
    }
    signatureUrl

    evidenceSighted

    templateName
    templateResponse {
      id
      responseData
    }

    type
    status
    worker {
      id
      mobile
      user {
        firstName
        lastName
        email
      }
      tickets {
        updatedAt
        ticketTemplate {
          shortName
        }
        reviews {
          reviewStatus
        }
      }
    }
    subcontractor {
      id
      company {
        name
      }
    }
    documentSignatures {
      ...documentSignatureAttributes
    }
    currentVersion {
      version
    }
    versionHistory {
      version
      induction {
        id
        status
        type
        createdAt
        archivedAt
      }
    }
  }
  ${DOCUMENT_SIGNATURE_ATTRIBUTES}
  ${WORKER_SNAPSHOT_ATTRIBUTES}
`

export const COMPANY_INDUCTION_ATTRIBUTES_SUBSET = gql`
  fragment companyInductionAttributesSubset on BuilderInduction {
    id
    createdAt

    type
    status
    worker {
      id
      user {
        firstName
        lastName
      }
      tickets {
        ticketTemplate {
          shortName
        }
        reviews {
          reviewStatus
        }
      }
    }
    subcontractor {
      id
      company {
        name
      }
    }
    currentVersion {
      version
    }
    versionHistory {
      version
    }
  }
`

export const GET_COMPANY_INDUCTIONS_LIST = gql`
  query companyInductionsList_UI(
    $builderId: String!
    $filter: BuilderInductionFilterInput
    $sort: InductionSortInput
    $pagination: PaginationInput
  ) {
    builderInductions(
      builderId: $builderId
      filter: $filter
      sort: $sort
      pagination: $pagination
    ) {
      ...companyInductionAttributesSubset
    }
    builderInductionsAggregation(
      builderId: $builderId
      filter: $filter
      pagination: $pagination
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${COMPANY_INDUCTION_ATTRIBUTES_SUBSET}
`

export const GET_COMPANY_INDUCTIONS_FOR_CSV_EXPORT = gql`
  query companyInductions_UI(
    $builderId: String!
    $filter: BuilderInductionFilterInput
  ) {
    builderInductions(builderId: $builderId, filter: $filter) {
      id
      createdAt
      worker {
        user {
          firstName
          lastName
        }
      }
      subcontractor {
        company {
          name
        }
      }
      status
    }
  }
`

export const GET_COMPANY_INDUCTION = gql`
  query companyInduction_UI($inductionId: String!) {
    builderInduction(id: $inductionId) {
      ...companyInductionAttributes
    }
  }
  ${COMPANY_INDUCTION_ATTRIBUTES}
`

export const GET_SITE_INDUCTIONS_LIST = gql`
  query siteInductionsList_UI(
    $projectId: String!
    $filter: InductionFilterInput
    $sort: InductionSortInput
    $pagination: PaginationInput
  ) {
    projectInductions(
      projectId: $projectId
      filter: $filter
      sort: $sort
      pagination: $pagination
    ) {
      ...siteInductionAttributesSubset
    }
    projectInductionsAggregation(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${SITE_INDUCTION_ATTRIBUTES_SUBSET}
`

export const GET_SITE_INDUCTIONS_FOR_CSV_EXPORT = gql`
  query siteInductions_UI($projectId: String!, $filter: InductionFilterInput) {
    projectInductions(projectId: $projectId, filter: $filter) {
      id
      identifier
      createdAt
      worker {
        user {
          firstName
          lastName
        }
      }
      subcontractor {
        company {
          name
        }
      }
      status
    }
  }
`

export const GET_SITE_INDUCTIONS_FOR_SEARCH = gql`
  query siteInductionsForSearch_UI(
    $projectId: String!
    $filter: InductionFilterInput
    $pagination: PaginationInput
  ) {
    projectInductions(
      projectId: $projectId
      filter: $filter
      pagination: $pagination
    ) {
      id
      createdAt
      identifier

      worker {
        user {
          firstName
          lastName
        }
      }

      status
    }
  }
`

export const GET_SITE_INDUCTION = gql`
  query siteInduction_UI($inductionId: String!) {
    projectInduction(id: $inductionId) {
      ...siteInductionAttributes
    }
  }
  ${SITE_INDUCTION_ATTRIBUTES}
`

export const GET_SITE_INDUCTION_BY_IDENTIFIER = gql`
  query siteInductionByIdentifier_UI(
    $projectId: String!
    $inductionIdentifier: Int!
  ) {
    projectInductionByIdentifier(
      projectId: $projectId
      inductionIdentifier: $inductionIdentifier
    ) {
      ...siteInductionAttributes
    }
  }
  ${SITE_INDUCTION_ATTRIBUTES}
`

export const GET_SITE_INDUCTION_BY_IDENTIFIER_PUBLIC = gql`
  query siteInductionByIdentifier_public_UI(
    $projectId: String!
    $inductionIdentifier: Int!
  ) {
    projectInductionByIdentifier_public(
      projectId: $projectId
      inductionIdentifier: $inductionIdentifier
    ) {
      workerId
      firstName
      lastName
      status
      signedOnAt
    }
  }
`

export const GET_SITE_INDUCTION_BY_WORKER_PUBLIC = gql`
  query siteInductionByWorker_public_UI(
    $projectId: String!
    $workerId: String!
  ) {
    projectInductionByWorker_public(
      projectId: $projectId
      workerId: $workerId
    ) {
      workerId
      firstName
      lastName
      status
      signedOnAt
    }
  }
`

// used by the status dropdown select
export const UPDATE_INDUCTION_STATUS = gql`
  mutation updateInductionStatus_UI(
    $id: String!
    $status: InductionStatus!
    $evidenceSighted: Boolean
    $updateTicketStatus: Boolean
  ) {
    updateInduction(
      input: {
        inductionId: $id
        status: $status
        evidenceSighted: $evidenceSighted
        updateTicketStatus: $updateTicketStatus
      }
    ) {
      id
      status
      evidenceSighted
    }
  }
`

export const SEND_INDUCTION_UPDATE_EMAIL = gql`
  mutation sendInductionUpdateEmail_UI(
    $inductionId: String!
    $customMessage: String
  ) {
    sendInductionUpdateEmail(
      input: { inductionId: $inductionId, customMessage: $customMessage }
    ) {
      serviceResponse
    }
  }
`

export const SEND_BULK_INDUCTION_UPDATE_EMAILS_ADMIN = gql`
  mutation sendBulkInductionUpdateEmailAdmin_UI(
    $input: SendBulkInductionUpdateEmailsInput_Admin!
  ) {
    sendBulkInductionUpdateEmails_admin(input: $input)
  }
`

export const CREATE_INDUCTION = gql`
  mutation createInduction_UI($input: CreateInductionInput!) {
    createInduction(input: $input) {
      id

      ... on ProjectInduction {
        identifier
      }
    }
  }
`

export const CREATE_INDUCTION_INVITE = gql`
  mutation createInductionInvite_UI(
    $subcontractorId: String!
    $projectId: String
  ) {
    createInductionInvite(
      input: {
        metadata: { subcontractorId: $subcontractorId, projectId: $projectId }
      }
    ) {
      id
      metadata
    }
  }
`

export const CREATE_INDUCTION_INVITES = gql`
  mutation createInductionInvites_UI(
    $subcontractorIds: [String!]!
    $projectId: String
  ) {
    createInductionInvites(
      input: {
        metadata: { subcontractorIds: $subcontractorIds, projectId: $projectId }
      }
    ) {
      id
      metadata
      batchId
    }
  }
`

export const SEND_INDUCTION_INVITE_EMAIL = gql`
  mutation sendInductionInviteEmail_UI(
    $subcontractorId: String!
    $contactIds: [String!]
    $projectId: String!
    $inviteId: String!
    $customMessage: String
  ) {
    sendInductionInviteEmail(
      input: {
        subcontractorId: $subcontractorId
        contactIds: $contactIds
        projectId: $projectId
        inviteId: $inviteId
        customMessage: $customMessage
      }
    ) {
      serviceResponse
    }
  }
`

export const ARCHIVE_INDUCTION = gql`
  mutation archiveInduction_UI($id: String!) {
    archiveInduction(id: $id) {
      id
    }
  }
`

export const RESTORE_INDUCTION = gql`
  mutation restoreInduction_UI($id: String!) {
    restoreInduction(id: $id) {
      id
    }
  }
`

export const GET_SITE_INDUCTION_FOR_WORKER = gql`
  query siteInductionForWorker_UI($projectId: String!) {
    projectInductionForWorker(projectId: $projectId) {
      id
      identifier
      createdAt
      updatedAt
      archivedAt

      templateName
      templateResponse {
        id
        responseData
      }

      project {
        id
        name
      }

      type
      status
      signatureUrl
    }
  }
`

export const GET_SITE_INDUCTION_BY_WORKER = gql`
  query siteInductionByWorker_UI($projectId: String!, $workerId: String!) {
    projectInductionByWorker(projectId: $projectId, workerId: $workerId) {
      id
      createdAt
      updatedAt
      archivedAt

      type
      status

      ... on ProjectInduction {
        identifier
        subcontractor {
          company {
            name
          }
        }
      }
    }
  }
`
